// src/api/profiles.ts
// API layer for Majengo Profile feature.

import apiClient from "../services/apiClient";

export interface ServiceItem {
  id?: string;
  name: string;
  description?: string;
  price?: string;
  image_url?: string | null;
  images?: string[];
  category?: string;
  sizes?: string;
  quantity_in_stock?: string;
  sold_out?: boolean;
}

export interface ProfileData {
  slug: string;
  tagline?: string;
  description?: string;          // updates businesses.description on save
  cover_image_url?: string;
  logo_url?: string;
  gallery_images?: string[];
  services?: ServiceItem[];
  google_place_id?: string;
  website_url?: string;
  whatsapp_number?: string;
  instagram_url?: string;
  twitter_url?: string;
  facebook_url?: string;
  template_id: string;
  is_published?: boolean;
  custom_domain?: string | null;
  custom_domain_status?: 'pending' | 'active' | 'failed' | null;
}

export interface ProfileTemplate {
  id: string;
  name: string;
  description: string;
  is_free: boolean;
  price_kes: number;
  max_gallery_images: number;
  sort_order: number;
  preview_image_url?: string;
}

export interface BusinessProfile {
  id: string;
  business_id: string;
  slug: string;
  tagline?: string;
  cover_image_url?: string;
  logo_url?: string;
  gallery_images: string[];
  services: ServiceItem[];
  google_place_id?: string;
  website_url?: string;
  whatsapp_number?: string;
  instagram_url?: string;
  twitter_url?: string;
  facebook_url?: string;
  template_id: string;
  is_published: boolean;
  custom_domain?: string | null;
  custom_domain_status?: 'pending' | 'active' | 'failed' | null;
  created_at: string;
  updated_at: string;
}

export interface MyProfileResponse {
  business: {
    id: string;
    name: string;
    category: string;
    description?: string;
    logo_url?: string;
    primary_contact_phone?: string;
    business_email?: string;
  };
  profile: BusinessProfile | null;
}

export interface UpsertProfileResponse {
  profile: BusinessProfile;
  public_url: string;
}

export interface QrCodeResponse {
  qr_base64: string;   // "data:image/png;base64,..."
  public_url: string;
  slug: string;
}

export interface UploadUrlResponse {
  key: string;
  uploadUrl: string;
  downloadUrl: string;
  expiresAt: string;
}

/** Fetch the owner's own profile for the edit screen */
export const fetchMyProfile = async (
  businessId: string
): Promise<MyProfileResponse> => {
  const response = await apiClient.get(`/businesses/${businessId}/profile`);
  return response.data;
};

/** Create or update a business profile */
export const upsertMyProfile = async (
  businessId: string,
  data: ProfileData
): Promise<UpsertProfileResponse> => {
  const response = await apiClient.post(
    `/businesses/${businessId}/profile`,
    data
  );
  return response.data;
};

/** Check if a slug is available (debounced in UI) */
export const checkSlugAvailability = async (
  businessId: string,
  slug: string
): Promise<{ available: boolean; slug: string }> => {
  const response = await apiClient.get(
    `/businesses/${businessId}/profile/check-slug`,
    { params: { slug } }
  );
  return response.data;
};

/** Fetch fresh QR code from backend (regenerated each call) */
export const fetchProfileQrCode = async (
  businessId: string,
  size = 600
): Promise<QrCodeResponse> => {
  const response = await apiClient.get(
    `/businesses/${businessId}/profile/qr-code`,
    { params: { size } }
  );
  return response.data;
};

/** Get a presigned R2 PUT URL for a profile image upload */
export const getProfileUploadUrl = async (
  businessId: string,
  fileName: string,
  contentType: string,
  imageType: "cover" | "gallery" | "service" = "gallery"
): Promise<UploadUrlResponse> => {
  const response = await apiClient.post(
    `/businesses/${businessId}/profile/upload-url`,
    { fileName, contentType, imageType }
  );
  return response.data;
};

/** Upload an image directly to R2 using a presigned URL */
export const uploadImageToR2 = async (
  uploadUrl: string,
  file: File | Blob,
  contentType: string
): Promise<void> => {
  await fetch(uploadUrl, {
    method: "PUT",
    headers: { "Content-Type": contentType },
    body: file,
  });
};

/** Fetch all available templates (free + premium) */
export const fetchProfileTemplates = async (): Promise<ProfileTemplate[]> => {
  const response = await apiClient.get("/businesses/public/templates");
  return response.data;
};

/** Add a custom domain to a profile */
export const addCustomDomain = async (
  businessId: string,
  domain: string
): Promise<{ success: boolean; domain: string; status: string }> => {
  const response = await apiClient.post(
    `/businesses/${businessId}/profile/custom-domain`,
    { domain }
  );
  return response.data;
};

/** Remove a custom domain from a profile */
export const removeCustomDomain = async (
  businessId: string
): Promise<{ success: boolean }> => {
  const response = await apiClient.delete(
    `/businesses/${businessId}/profile/custom-domain`
  );
  return response.data;
};
